/**
 * Game Utility Functions
 * Reusable helper functions for game logic
 * 
 * Phase 2 Refactoring - Extracted from useGameActions.js
 */

import { 
  getActionName, 
  ANIMATION_DURATIONS,
  AUDIO_SETTINGS 
} from '@/config/gameConstants';

// ========== AUDIO UTILITIES ==========

/**
 * Create and configure an Audio instance
 * @param {string} audioPath - Path to audio file
 * @param {object} settings - Audio settings { volume, startTime }
 * @returns {Audio} Configured Audio instance
 */
export const createAudioInstance = (audioPath, settings = {}) => {
  const audio = new Audio(audioPath);
  
  if (settings.volume !== undefined) {
    audio.volume = settings.volume;
  }
  
  if (settings.startTime !== undefined) {
    audio.currentTime = settings.startTime;
  }
  
  return audio;
};

/**
 * Create Web Audio API context with gain node for volume boost
 * @param {Audio} audioElement - HTML Audio element
 * @param {number} gainValue - Gain multiplier (e.g., 2.0 for 200% volume)
 * @returns {object} { audioContext, source, gainNode }
 */
export const createAudioContextWithGain = (audioElement, gainValue = 1.0) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const source = audioContext.createMediaElementSource(audioElement);
  const gainNode = audioContext.createGain();
  
  gainNode.gain.value = gainValue;
  
  source.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  return { audioContext, source, gainNode };
};

/**
 * Play audio with error handling
 * @param {Audio} audio - Audio instance to play
 * @param {string} errorContext - Context for error logging
 */
export const playAudioSafely = (audio, errorContext = 'Audio') => {
  if (!audio) return;
  
  audio.play().catch(err => {
    console.log(`${errorContext} play failed:`, err);
  });
};

/**
 * Stop all audio elements in the DOM
 */
export const stopAllDomAudio = () => {
  document.querySelectorAll('audio').forEach(audio => {
    audio.pause();
    audio.currentTime = 0;
  });
};

// ========== ACTION UTILITIES ==========

/**
 * Format action notation for display
 * @param {string} actionType - Type of action
 * @param {string} sourceCode - Source country code
 * @param {string} targetCode - Target country code
 * @returns {string} Formatted action notation (e.g., "PROPAGANDA: USA → CHN")
 */
export const formatActionNotation = (actionType, sourceCode, targetCode) => {
  const actionName = getActionName(actionType);
  return `${actionName}: ${sourceCode} → ${targetCode}`;
};

/**
 * Determine if action should trigger dialogue
 * @param {string} actionType - Type of action
 * @param {boolean} success - Whether action succeeded
 * @param {string} targetCode - Target country code (optional)
 * @returns {boolean} Whether to show dialogue
 */
export const shouldShowDialogue = (actionType, success, targetCode = null) => {
  // Only show dialogue for these action types
  const dialogueActions = ['propaganda', 'diplomacy', 'blackmail', 'deploy_troops', 'air_strike'];
  
  if (!dialogueActions.includes(actionType)) {
    return false;
  }
  
  // Show dialogue on success, or failure with a target
  return success || (targetCode !== null);
};

/**
 * Determine if action is a hostile action that warrants "attacked" response
 * @param {string} actionType - Type of action
 * @returns {boolean} Whether action is hostile
 */
export const isHostileAction = (actionType) => {
  return ['propaganda', 'blackmail', 'deploy_troops', 'nuclear_strike', 'air_strike'].includes(actionType);
};

// ========== ANIMATION UTILITIES ==========

/**
 * Get animation duration for an action
 * @param {string} actionType - Type of action
 * @param {boolean} isAI - Whether this is an AI action
 * @param {boolean} success - Whether action succeeded (relevant for air_strike)
 * @returns {number} Duration in milliseconds
 */
export const getAnimationDuration = (actionType, isAI = false, success = true) => {
  // Special case: air strike duration depends on success/failure
  if (actionType === 'air_strike') {
    if (isAI) {
      return success ? ANIMATION_DURATIONS.aiAirStrikeSuccess : ANIMATION_DURATIONS.aiAirStrikeFailed;
    } else {
      return success ? ANIMATION_DURATIONS.airStrikeSuccess : ANIMATION_DURATIONS.airStrikeFailed;
    }
  }
  
  // Nuclear strikes
  if (actionType === 'nuclear_strike') {
    return isAI ? ANIMATION_DURATIONS.aiNuclearStrike : ANIMATION_DURATIONS.nuclearStrike;
  }
  
  // Other actions
  const prefix = isAI ? 'ai' : '';
  const key = prefix + actionType.charAt(0).toUpperCase() + actionType.slice(1).replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  
  return ANIMATION_DURATIONS[key] || ANIMATION_DURATIONS.propaganda;
};

/**
 * Get dialogue timing for an action
 * @param {string} actionType - Type of action
 * @param {boolean} success - Whether action succeeded
 * @returns {number} Delay in milliseconds before showing dialogue
 */
export const getDialogueTiming = (actionType, success = true) => {
  if (actionType === 'air_strike') {
    return success ? ANIMATION_DURATIONS.airStrikeDialogue : ANIMATION_DURATIONS.airStrikeFailedDialogue;
  }
  
  const timingKey = `${actionType}Dialogue`;
  return ANIMATION_DURATIONS[timingKey] || 4000; // Default 4 seconds
};

// ========== GAME STATE UTILITIES ==========

/**
 * Check if player can afford an action
 * @param {object} country - Country object with resources
 * @param {object} requirements - Resource requirements { money, military, influence, etc. }
 * @returns {boolean} Whether country can afford the action
 */
export const canAffordAction = (country, requirements) => {
  if (!country || !country.resources || !requirements) {
    return false;
  }
  
  return Object.entries(requirements).every(([resource, amount]) => {
    return (country.resources[resource] || 0) >= amount;
  });
};

/**
 * Get player country from game state
 * @param {object} game - Game state object
 * @returns {object|null} Player country or null
 */
export const getPlayerCountry = (game) => {
  return game?.countries?.find(c => c.is_player) || null;
};

/**
 * Get country by code
 * @param {object} game - Game state object
 * @param {string} code - Country code
 * @returns {object|null} Country object or null
 */
export const getCountryByCode = (game, code) => {
  return game?.countries?.find(c => c.code === code) || null;
};

/**
 * Check if country is eliminated
 * @param {object} country - Country object
 * @returns {boolean} Whether country is eliminated
 */
export const isCountryEliminated = (country) => {
  if (!country) return false;
  
  return !!(
    country.is_eliminated || 
    country.eliminated || 
    country.resources?.eliminated || 
    (country.resources?.population || 0) <= 0
  );
};

// ========== EVENT UTILITIES ==========

/**
 * Dispatch custom victory event
 * @param {object} defeatedCountry - Defeated country details { leaderName, countryName, countryCode }
 * @param {object} stats - Victory stats { airstrikes, blackmail, propaganda, deployTroops, totalDamage }
 */
export const dispatchVictoryEvent = (defeatedCountry, stats = {}) => {
  const victoryEvent = new CustomEvent('countryDefeated', {
    detail: {
      defeatedCountry: {
        leaderName: defeatedCountry.leaderName || defeatedCountry.leader_name,
        countryName: defeatedCountry.countryName || defeatedCountry.country_name,
        countryCode: defeatedCountry.countryCode || defeatedCountry.country_code || defeatedCountry.countryName
      },
      stats: {
        airstrikes: stats.airstrikes || 0,
        blackmail: stats.blackmail || 0,
        propaganda: stats.propaganda || 0,
        deployTroops: stats.deployTroops || 0,
        totalDamage: stats.totalDamage || 0
      }
    }
  });
  
  window.dispatchEvent(victoryEvent);
};

// ========== LOGGING UTILITIES ==========

/**
 * Create AI action log entry
 * @param {object} action - AI action object
 * @returns {object} Log entry for summary modal
 */
export const createAiActionLogEntry = (action) => {
  return {
    sourceCountry: action.country_code,
    sourceCountryName: action.country,
    sourceCountryColor: action.color,
    targetCountry: action.target_code,
    targetCountryName: action.target,
    actionType: action.action,
    success: action.success,
    enrichmentGained: 0, // Populated from game state if needed
    newEnrichment: 0
  };
};

/**
 * Log action with formatted output
 * @param {string} emoji - Emoji prefix
 * @param {string} message - Log message
 * @param {object} details - Additional details (optional)
 */
export const logAction = (emoji, message, details = null) => {
  if (details) {
    console.log(`${emoji} ${message}`, details);
  } else {
    console.log(`${emoji} ${message}`);
  }
};

// ========== VALIDATION UTILITIES ==========

/**
 * Validate game state has required properties
 * @param {object} game - Game state
 * @returns {boolean} Whether game state is valid
 */
export const isValidGameState = (game) => {
  return !!(
    game &&
    game.id &&
    game.countries &&
    Array.isArray(game.countries) &&
    game.countries.length > 0
  );
};

/**
 * Validate action result
 * @param {object} result - Action result from backend
 * @returns {boolean} Whether result is valid
 */
export const isValidActionResult = (result) => {
  return !!(
    result &&
    typeof result.success === 'boolean' &&
    result.message
  );
};
